import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Stack,
  Text,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { FaBook } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { ManualType } from './api/ManualApi';
import { InputLimits } from '../contexts/constants';

type HowToVideoItemType = 'directory' | 'link';

type ManualAddModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: { manualName: string; manualLink: string; isAdminOnly: boolean; type?: HowToVideoItemType }) => void;
  onUpdate?: (data: { manualId: string; manualName: string; manualLink: string; isAdminOnly: boolean }) => void;
  formData: ManualType | null;
  isUpdate: boolean;
  titleKey?: string;
  nameKey?: string;
  showAdminOnlyOption?: boolean;
  /** 操作説明動画タブでのみ true にし、ディレクトリ/動画リンクの種別選択UIを表示する */
  showTypeSelector?: boolean;
  /** 編集時の種別固定表示に使用（type変更は不可） */
  currentType?: HowToVideoItemType;
};

/**
 * HTTPS URLの形式チェック
 * バックエンド（入力検証 isHttpsUrl）と同じ条件にする: "https://" で始まり、上限以内で、空白・制御文字を含まないこと
 * （これらを満たさない値は、保存時にバックエンドで拒否される）
 * @param url 前後の空白を除去した値
 */
const isValidHttpsURL = (url: string): boolean => {
  if (!url.startsWith("https://") || url.length > InputLimits.URL || /[\s\x00-\x1f\x7f]/.test(url)) return false;
  try {
    const parsedUrl = new URL(url);
    return parsedUrl.protocol === 'https:';
  } catch {
    return false;
  }
};

/**
 * マニュアル追加/更新モーダル
 */
const ManualAddModal: React.FC<ManualAddModalProps> = ({
  isOpen,
  onClose,
  onSave,
  onUpdate,
  formData,
  isUpdate,
  titleKey = 'components.ManualAddModal.title',
  nameKey = 'components.ManualAddModal',
  showAdminOnlyOption = true,
  showTypeSelector = false,
  currentType,
}) => {
  const { t } = useTranslation();
  const [manualName, setManualName] = useState<string>('');
  const [manualLink, setManualLink] = useState<string>('');
  const [isAdminOnly, setIsAdminOnly] = useState<boolean>(false);
  const [selectedType, setSelectedType] = useState<HowToVideoItemType>('link');
  const [errors, setErrors] = useState<{ manualName?: string; manualLink?: string }>({});

  useEffect(() => {
    if (!isOpen) return;

    if (isUpdate && formData) {
      setManualName(formData.manualName);
      setManualLink(formData.manualLink);
      setIsAdminOnly(formData.isAdminOnly ?? false);
      setSelectedType(currentType ?? 'link');
    } else {
      setManualName('');
      setManualLink('');
      setIsAdminOnly(false);
      setSelectedType('link');
    }
    setErrors({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isUpdate, formData]);

  const isDirectoryType = showTypeSelector && selectedType === 'directory';

  /**
   * バリデーション
   */
  const validate = (): boolean => {
    const newErrors: { manualName?: string; manualLink?: string } = {};

    if (!manualName.trim()) {
      newErrors.manualName = t('components.ManualAddModal.error.manualNameRequired');
    }

    if (!isDirectoryType && !isValidHttpsURL(manualLink.trim())) {
      newErrors.manualLink = t('components.ManualAddModal.error.invalidHttpsUrl');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * 保存処理
   */
  const handleSave = () => {
    if (!validate()) return;

    if (isUpdate && formData) {
      onUpdate?.({
        manualId: formData.manualId,
        manualName: manualName.trim(),
        manualLink: isDirectoryType ? '' : manualLink.trim(),
        isAdminOnly,
      });
    } else {
      onSave({
        manualName: manualName.trim(),
        manualLink: isDirectoryType ? '' : manualLink.trim(),
        isAdminOnly,
        ...(showTypeSelector ? { type: selectedType } : {}),
      });
    }
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="2xl" closeOnEsc={false}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Box display="flex" alignItems="center" gap={2}>
            <FaBook color="#2B6CB0" />
            <Text fontWeight="bold">
              {isUpdate ? t(`${titleKey}.edit`) : t(`${titleKey}.add`)}
            </Text>
          </Box>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Stack spacing={4}>
            <Box maxH="400px" overflowY="auto" p={3}>
              {showTypeSelector && (
                <FormControl mb={4}>
                  <FormLabel>{t('components.ManualAddModal.howToVideoType')}</FormLabel>
                  {isUpdate ? (
                    <Text>
                      {selectedType === 'directory'
                        ? t('components.ManualAddModal.howToVideoTypeDirectory')
                        : t('components.ManualAddModal.howToVideoTypeLink')}
                    </Text>
                  ) : (
                    <RadioGroup value={selectedType} onChange={(value) => setSelectedType(value as HowToVideoItemType)}>
                      <Stack direction="row" spacing={5}>
                        <Radio value="directory">{t('components.ManualAddModal.howToVideoTypeDirectory')}</Radio>
                        <Radio value="link">{t('components.ManualAddModal.howToVideoTypeLink')}</Radio>
                      </Stack>
                    </RadioGroup>
                  )}
                </FormControl>
              )}

              <FormControl isRequired isInvalid={!!errors.manualName} mb={4}>
                <FormLabel>
                  {t(isDirectoryType ? `${nameKey}.label.directoryName` : `${nameKey}.label.manualName`)}
                </FormLabel>
                <Input
                  placeholder={t(isDirectoryType ? `${nameKey}.placeholder.directoryName` : `${nameKey}.placeholder.manualName`)}
                  value={manualName}
                  onChange={(e) => setManualName(e.target.value)}
                  maxLength={InputLimits.MANUAL_NAME}
                />
                {errors.manualName && (
                  <FormErrorMessage>{errors.manualName}</FormErrorMessage>
                )}
              </FormControl>

              {!isDirectoryType && (
                <FormControl isRequired isInvalid={!!errors.manualLink}>
                  <FormLabel>{t('components.ManualAddModal.label.manualLink')}</FormLabel>
                  <Input
                    placeholder={t('components.ManualAddModal.placeholder.manualLink')}
                    value={manualLink}
                    onChange={(e) => setManualLink(e.target.value)}
                    maxLength={InputLimits.URL}
                  />
                  {errors.manualLink && (
                    <FormErrorMessage>{errors.manualLink}</FormErrorMessage>
                  )}
                </FormControl>
              )}

              {showAdminOnlyOption && (
                <FormControl mt={4}>
                  <Checkbox isChecked={isAdminOnly} onChange={(e) => setIsAdminOnly(e.target.checked)}>
                    {t('components.ManualAddModal.label.isAdminOnly')}
                  </Checkbox>
                </FormControl>
              )}
            </Box>
          </Stack>
        </ModalBody>
        <ModalFooter>
          <Button colorScheme="blue" mr={3} onClick={handleSave}>
            {t('common.button.save')}
          </Button>
          <Button colorScheme="gray" onClick={onClose}>
            {t('common.button.cancel')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ManualAddModal;
